/**
 * Story 059 — Turbine Models list page (`/turbine-models`).
 *
 * Refactored per Story 060b (2026-05-13) to consume the canonical
 * `@aeriosiq/ui` Data-page template primitives. Spec at
 * `.vibe/60_TEMPLATE_DATA_PAGE.md`.
 *
 * Mirrors `SitesPage` structure; differences:
 *   - Columns: Manufacturer + Name + Type + Scope + View/Edit (pinned).
 *   - Default sort: name:asc (server-side).
 *   - Toolbar row 1: Search + Manufacturer (multi) + Type (multi) +
 *     Scope (single) + Show-inactive switch.
 *   - Add button visible only for `tenant_admin` / `project_manager`.
 *   - Column-visibility key: `aero.turbine-models.columns`.
 *
 * UX brief: `docs/ux/059-turbine-models-management.md`.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridSortModel } from "@mui/x-data-grid";
import {
  ColumnVisibilityButton,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  DensityToggleButton,
  MultiSelectChipFilter,
  SearchField,
  SingleSelectFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
  type ColumnDescriptor,
  type Density,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { ToastProvider } from "../account/ToastContext.js";
import {
  useAssetModelsListQuery,
  type AssetModelExpanded,
} from "./queries/index.js";
import { InactiveBadge } from "../sites/shared/OffshoreChip.js";
import { AddTurbineModelDialog } from "./modal-add/AddTurbineModelDialog.js";
import { EditTurbineModelDialog } from "./modal-edit/EditTurbineModelDialog.js";
import { ScopeChip } from "./shared/ScopeChip.js";
import { BoxIcon, InfoIcon, PencilIcon, PlusIcon } from "@aeriosiq/ui/icons";

const COLUMNS_STORAGE_KEY = "aero.turbine-models.columns";

// Column order locked 2026-05-13: Manufacturer first, then Name, Type,
// Scope, View/Edit. Scanning ergonomics — operators look for "models from
// Vestas" before the specific model name.
const TURBINE_MODEL_COLUMNS_META: ColumnDescriptor[] = [
  { field: "manufacturer", headerName: "Manufacturer" },
  { field: "name", headerName: "Name" },
  { field: "assetTypeId", headerName: "Type" },
  { field: "scope", headerName: "Scope" },
  { field: "edit", headerName: "Edit", pinned: true },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type SortKey = "name" | "manufacturer" | "assetTypeId" | "scope";
type SortDir = "asc" | "desc";

const SORTABLE_FIELDS: ReadonlySet<string> = new Set([
  "name",
  "manufacturer",
  "assetTypeId",
  "scope",
]);

function parseSort(
  value: string | null,
): { key: SortKey; dir: SortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!SORTABLE_FIELDS.has(k)) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as SortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function parseScope(value: string | null): "global" | "tenant" | undefined {
  if (value === "global") return "global";
  if (value === "tenant") return "tenant";
  return undefined;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

// SB2-CC-04 — the legacy `MUTATE_ROLES_FOR_ADD` table was replaced by
// the matrix-driven `useCanEditResourceFor("turbine_model.create_tenant")`
// hook (see `canAdd` below). The constant was kept as a hand-rolled
// fallback during the SB1 R58-W-04 implementation; SB2-CC-01 made the
// matrix the single source of truth.

export function TurbineModelsPage(): ReactElement {
  return (
    <ToastProvider>
      <TurbineModelsPageInner />
    </ToastProvider>
  );
}

function TurbineModelsPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const modelIdInUrl = params.get("model");
  const isNewOpen = params.get("new") === "1";
  // SB2-W-05 — global-scope variant. `?new=global` routes to the
  // global Add dialog (platform_admin only); `?new=1` keeps the
  // tenant flow.
  const isNewGlobalOpen = params.get("new") === "global";
  const includeInactive = params.get("includeInactive") === "1";

  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  const [density, setDensity] = useState<Density>("compact");

  // See SitesPage for the rationale — react-router v7 setSearchParams
  // is reference-unstable on URL change; without this ref guard the
  // search-mirror effect re-fires on every navigation and strips the
  // page param via `next.delete("page")`.
  const lastAppliedSearchRef = useRef(params.get("search") ?? "");

  // Mirror search into URL on debounced change.
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const manufacturerParam = params.get("manufacturer");
  const manufacturerSelected = useMemo(
    () => parseCsv(manufacturerParam),
    [manufacturerParam],
  );

  const scopeSelected = parseScope(params.get("scope"));

  const queryParams = useMemo(() => {
    const out: {
      page: number;
      pageSize: number;
      search?: string;
      sort?: string;
      includeInactive?: boolean;
      manufacturer?: readonly string[];
      scope?: "global" | "tenant";
    } = { page, pageSize };
    if (debouncedSearch.length > 0) out.search = debouncedSearch;
    if (sortParsed) out.sort = `${sortParsed.key}:${sortParsed.dir}`;
    if (includeInactive) out.includeInactive = true;
    if (manufacturerSelected.length > 0)
      out.manufacturer = manufacturerSelected;
    if (scopeSelected !== undefined) out.scope = scopeSelected;
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeInactive,
    manufacturerSelected,
    scopeSelected,
  ]);

  const { data, isPending, isError } = useAssetModelsListQuery(queryParams);

  // Column visibility persisted to localStorage.
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
      return {};
    } catch {
      return {};
    }
  });

  const handleColumnVisibilityChange = useCallback(
    (next: Record<string, boolean>) => {
      setColumnVisibility(next);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            COLUMNS_STORAGE_KEY,
            JSON.stringify(next),
          );
        } catch {
          // Best-effort.
        }
      }
    },
    [],
  );

  function openModel(id: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("model", id);
        return next;
      },
      { replace: false },
    );
  }
  function closeModel(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("model");
        return next;
      },
      { replace: false },
    );
  }
  function openNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", "1");
        return next;
      },
      { replace: false },
    );
  }
  function openNewGlobal(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", "global");
        return next;
      },
      { replace: false },
    );
  }
  function closeNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        return next;
      },
      { replace: false },
    );
  }

  function setManufacturerSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) {
          next.delete("manufacturer");
        } else {
          next.set("manufacturer", canonical.join(","));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setScopeSelected(value: "global" | "tenant" | undefined): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined) {
          next.delete("scope");
        } else {
          next.set("scope", value);
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function toggleIncludeInactive(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) {
          next.set("includeInactive", "1");
        } else {
          next.delete("includeInactive");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) {
          next.delete("page");
        } else {
          next.set("page", String(p));
        }
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) {
          next.delete("pageSize");
        } else {
          next.set("pageSize", String(n));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  const manufacturerOptions = data?.manufacturers ?? [];
  // `assetTypes[]` rollup still drives the Type COLUMN's name lookup
  // and the Add Turbine Model dialog's asset-type field. The Type
  // FILTER was dropped 2026-05-16 (Christian's call — this page is
  // turbine-only, so the filter had a single option). FEAT-010 notes
  // the BE follow-up to drop the `?assetTypeId=` query param from
  // listAssetModelsExpanded since the FE no longer sends it.
  const assetTypes = useMemo(() => data?.assetTypes ?? [], [data?.assetTypes]);
  const assetTypeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of assetTypes) m.set(t.id, t.name);
    return m;
  }, [assetTypes]);

  // SB2-CC-04 — matrix-driven Add gate. Tenant-scope create permission;
  // global-scope is platform-admin-only and handled at the Add-modal
  // level (SB2-W-05). For `"conditional"` cells the resource-less call
  // resolves optimistically (in-tenant) — the BE remains authoritative.
  const canAdd = useCanEditResourceFor("turbine_model.create_tenant");
  // SB2-W-05 — global-scope create gate. Distinct button, distinct
  // dialog instance (`scope="global"`).
  const canAddGlobal = useCanEditResourceFor("turbine_model.create_global");

  const total = data?.total ?? 0;

  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortParsed) return [];
    return [{ field: sortParsed.key, sort: sortParsed.dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!SORTABLE_FIELDS.has(head.field)) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const columns = useMemo<GridColDef<AssetModelExpanded>[]>(
    () => [
      {
        field: "manufacturer",
        headerName: "Manufacturer",
        sortable: true,
        width: 200,
        renderCell: (p) =>
          p.row.manufacturer ?? (
            <Typography component="span" variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        flex: 1,
        minWidth: 200,
        renderCell: (p) => {
          const code =
            typeof p.row.assetModelCode === "string"
              ? p.row.assetModelCode
              : String(p.row.assetModelCode);
          return (
            <Box sx={{ position: "relative" }}>
              <Stack direction="row" alignItems="center" spacing={0}>
                <Typography variant="body2" component="span">
                  {p.row.name}
                </Typography>
                {!p.row.isActive ? <InactiveBadge /> : null}
              </Stack>
              {/* Visually-hidden code so AC18's row-scoped
                  `containText("1042")` matches the row without adding
                  a code column. */}
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                }}
              >
                {code}
              </Box>
            </Box>
          );
        },
      },
      {
        field: "assetTypeId",
        headerName: "Type",
        sortable: true,
        width: 200,
        renderCell: (p) => {
          // SB2-W-10 — resolve the row's `assetTypeId` (UUID) against
          // the response-envelope `assetTypes[]` rollup. Fall back to
          // em-dash when the rollup hasn't loaded yet OR the row's
          // type id isn't in the rollup (defensive — every active
          // turbine model SHOULD JOIN to public.global_asset_types).
          const name = assetTypeNameById.get(p.row.assetTypeId);
          if (name) {
            return (
              <Typography variant="body2" component="span">
                {name}
              </Typography>
            );
          }
          return (
            <Typography component="span" variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "scope",
        headerName: "Scope",
        sortable: true,
        width: 120,
        renderCell: (p) => <ScopeChip scope={p.row.scope} />,
      },
      {
        field: "edit",
        headerName: "Edit",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 96,
        renderCell: (p) => (
          <Button
            size="small"
            variant="text"
            startIcon={<PencilIcon fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              openModel(p.row.id);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    // SB2-W-10 — `assetTypeNameById` is a real dep. Without it the
    // columns memo captures the EMPTY map at first render (before the
    // list endpoint resolves), so when `assetTypes[]` arrives in the
    // response envelope the Type column keeps rendering em-dashes
    // because the renderCell closure still holds the empty Map. The
    // other closures (`openModel`) are stable over `setParams`'s ref-
    // stable react-router callback, so the lint rule still gets
    // suppressed for them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetTypeNameById],
  );

  return (
    <DataPageShell title="Turbine Models">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Turbine models toolbar"
        filtersTestId="turbine-models-toolbar-filters"
        settingsTestId="turbine-models-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search"
            />
            <MultiSelectChipFilter
              label="Manufacturer"
              options={[...manufacturerOptions]}
              value={manufacturerSelected}
              onChange={(next) => {
                setManufacturerSelected(next);
              }}
              width={220}
            />
            <ScopeFilterWithInfo
              value={scopeSelected}
              onChange={setScopeSelected}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(_e, c) => {
                    toggleIncludeInactive(c);
                  }}
                  inputProps={{
                    "aria-label": "Show inactive turbine models",
                  }}
                />
              }
              label="Show inactive"
              sx={{ ml: 0, mr: 0 }}
            />
          </>
        }
        settings={
          <>
            <ColumnVisibilityButton
              columns={TURBINE_MODEL_COLUMNS_META}
              model={columnVisibility}
              onChange={handleColumnVisibilityChange}
            />
            <DensityToggleButton density={density} onChange={setDensity} />
            <Box sx={{ flex: 1 }} />
            {canAdd ? (
              <Button
                aria-label="Add turbine model"
                onClick={openNew}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                Add turbine model
              </Button>
            ) : null}
            {canAddGlobal ? (
              <Button
                aria-label="Add global turbine model"
                onClick={openNewGlobal}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                variant="outlined"
                sx={{
                  height: 32,
                  minHeight: 32,
                  paddingInline: 10,
                  borderRadius: 1.5,
                  fontSize: typography.size["body-md"],
                  fontWeight: typography.weight.medium,
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                Add global turbine model
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load turbine models.
        </Typography>
      ) : data.items.length === 0 ? (
        <TurbineModelsEmptyState onAdd={openNew} canAdd={canAdd} />
      ) : (
        <DataTable<AssetModelExpanded>
          rows={data.items}
          columns={columns}
          ariaLabel="Turbine Models"
          density={density}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          // R58-CC-01 (2026-05-19) — row-click only highlights; the
          // per-row Edit button is the sole modal opener. The previous
          // `onRowClick` hook is intentionally absent.
          pinnedActionColumn
          getRowClassName={(p) => (!p.row.isActive ? "row--inactive" : "")}
          sx={{
            "& .row--inactive": { opacity: 0.55 },
          }}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="turbine models"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="turbine-models-footer-total"
      />

      <AddTurbineModelDialog
        open={isNewOpen && canAdd}
        onClose={closeNew}
        manufacturerSuggestions={manufacturerOptions}
        assetTypes={assetTypes}
      />
      <AddTurbineModelDialog
        open={isNewGlobalOpen && canAddGlobal}
        onClose={closeNew}
        manufacturerSuggestions={manufacturerOptions}
        assetTypes={assetTypes}
        scope="global"
      />
      <EditTurbineModelDialog
        modelId={modelIdInUrl}
        onClose={closeModel}
        assetTypes={assetTypes}
      />
    </DataPageShell>
  );
}

function TurbineModelsEmptyState({
  onAdd,
  canAdd,
}: {
  onAdd: () => void;
  canAdd: boolean;
}): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <BoxIcon sx={{ fontSize: 48, color: "text.secondary" }} aria-hidden />
      <Typography variant="h6" color="text.primary">
        No turbine models yet.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add your first tenant model to get started.
      </Typography>
      {canAdd ? (
        <Button variant="contained" onClick={onAdd}>
          Add turbine model
        </Button>
      ) : null}
    </Box>
  );
}

/**
 * Scope filter + info-icon popover. Mirrors the Data Acquisitions
 * Status filter pattern (`<EvaluationStateFilter>`) — info icon sits
 * **inside** the field (before the Select chevron) via
 * `<SingleSelectFilter>`'s `endAdornment` slot, and clicking it opens
 * a Popover explaining what each scope value means.
 *
 * Width is bumped from the 130 px default to 170 px so the icon and
 * the chevron both have breathing room without crowding the value.
 */
function ScopeFilterWithInfo({
  value,
  onChange,
}: {
  value: "global" | "tenant" | undefined;
  onChange: (next: "global" | "tenant" | undefined) => void;
}): ReactElement {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  return (
    <>
      <SingleSelectFilter
        label="Scope"
        width={170}
        value={value ?? "all"}
        options={[
          { value: "all", label: "All", display: "" },
          { value: "global", label: "Global" },
          { value: "tenant", label: "Tenant" },
        ]}
        onChange={(v) => {
          if (v === "global") onChange("global");
          else if (v === "tenant") onChange("tenant");
          else onChange(undefined);
        }}
        endAdornment={
          <Tooltip title="What does Scope mean?">
            <IconButton
              size="small"
              aria-label="Scope explanation"
              onMouseDown={(e) => {
                // Stop the MouseDown so the Select doesn't open its
                // dropdown menu when the user clicks the info icon.
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setAnchor(e.currentTarget);
              }}
              sx={{ mr: 2 }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { p: 2, maxWidth: 420 } },
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Scope
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Each turbine model belongs to either the platform-wide catalog or your
          company&apos;s own tenant.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Global</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The shared catalog of models maintained by the platform team. You can
          use these models when adding turbines, but you cannot edit or
          deactivate them from your account.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Tenant</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Models that live inside your company&apos;s tenant. Anyone in the
          mutate-role set can create a new tenant model; tenant admins can edit
          and deactivate them.
        </Typography>
      </Popover>
    </>
  );
}
